"use client";

import type {
  Dashboard,
  DashboardSection,
  DashboardType,
  DashboardVariableConfig,
  DashboardWidget,
} from "@/types/dashboard";
import { apiClient } from "./api";

export interface CreateDashboardRequest {
  title: string;
  description?: string;
  icon?: string;
  showInSidebar?: boolean;
  customerId?: string;
  isDefault?: boolean;
  tags?: string[];
  type?: DashboardType;
  sections?: DashboardSection[];
  widgets?: DashboardWidget[];
  settings?: Dashboard["settings"];
  variableConfig?: DashboardVariableConfig;
  isLocked?: boolean;
  group?: string; // Dashboard grouping for sidebar organization
}

export interface UpdateDashboardRequest {
  title?: string;
  description?: string;
  icon?: string;
  showInSidebar?: boolean;
  sections?: DashboardSection[];
  widgets?: DashboardWidget[];
  settings?: Dashboard["settings"];
  metadata?: Partial<Dashboard["metadata"]>;
  type?: DashboardType;
  variableConfig?: DashboardVariableConfig;
  isLocked?: boolean;
  group?: string; // Dashboard grouping for sidebar organization
}

// Go backend dashboard response type
interface GoDashboardResponse {
  id: string;
  entityType: string;
  entityId?: string;
  scope: string;
  title: string;
  description?: string;
  icon?: string;
  layout?: string | { sections?: DashboardSection[] };
  settings?: string | Dashboard["settings"];
  isPublic: boolean;
  isTemplate: boolean;
  version: number;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  widgets?: DashboardWidget[];
  // Frontend-specific fields that may be included
  type?: DashboardType;
  showInSidebar?: boolean;
  customerId?: string;
  sections?: DashboardSection[];
  metadata?: {
    tags?: string[];
  };
  isLocked?: boolean;
}

// User role type for dashboard access
export type UserRoleType = "system" | "integrator" | "organization";

// Go backend paginated list response
interface GoDashboardListResponse {
  dashboards: GoDashboardResponse[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  userRole?: UserRoleType;
  entityId?: string;
}

// Go backend create dashboard request
interface GoCreateDashboardRequest {
  title: string;
  description?: string;
  icon?: string;
  layout?: string;
  settings?: string;
  isPublic: boolean;
  isTemplate: boolean;
}

// Go backend update dashboard request
interface GoUpdateDashboardRequest {
  title?: string;
  description?: string;
  icon?: string;
  layout?: string;
  settings?: string;
  isPublic?: boolean;
}

// Parse layout from backend (can be string or object)
// Returns both sections and widgets stored in the layout JSON
function parseLayout(
  layout:
    | string
    | { sections?: DashboardSection[]; widgets?: DashboardWidget[] }
    | undefined
): { sections: DashboardSection[]; widgets: DashboardWidget[] } {
  if (!layout) return { sections: [], widgets: [] };
  if (typeof layout === "string") {
    try {
      const parsed = JSON.parse(layout) as {
        sections?: DashboardSection[];
        widgets?: DashboardWidget[];
      };
      return {
        sections: parsed.sections || [],
        widgets: parsed.widgets || [],
      };
    } catch {
      return { sections: [], widgets: [] };
    }
  }
  return {
    sections: layout.sections || [],
    widgets: layout.widgets || [],
  };
}

// Extended settings type that includes variableConfig and group for storage
interface ExtendedSettings {
  gridSize: number;
  gap: number;
  enableSnapToGrid: boolean;
  backgroundColor: string;
  type?: DashboardType;
  variableConfig?: DashboardVariableConfig;
  group?: string; // Dashboard grouping for sidebar organization
}

// Parse settings from backend (can be string or object)
function parseSettings(
  settings: string | Dashboard["settings"] | undefined
): ExtendedSettings {
  const defaultSettings: ExtendedSettings = {
    gridSize: 12,
    gap: 16,
    enableSnapToGrid: true,
    backgroundColor: "#ffffff",
  };

  if (!settings) return defaultSettings;
  if (typeof settings === "string") {
    try {
      return JSON.parse(settings) as ExtendedSettings;
    } catch {
      return defaultSettings;
    }
  }
  return settings as ExtendedSettings;
}

// Transform backend response to frontend Dashboard format
function transformBackendDashboard(data: GoDashboardResponse): Dashboard {
  // Parse layout which now contains both sections and widgets
  const layoutData = parseLayout(data.layout);
  const parsedSettings = parseSettings(data.settings);

  // Extract variableConfig and group from settings (stored together in JSON)
  const {
    variableConfig,
    type: settingsType,
    group,
    ...baseSettings
  } = parsedSettings;

  return {
    id: data.id,
    title: data.title || "Untitled Dashboard",
    description: data.description || "",
    customerId: data.entityId || data.customerId,
    type: settingsType || data.type || "section",
    icon: data.icon,
    showInSidebar: data.showInSidebar ?? true,
    sections: data.sections || layoutData.sections,
    widgets: data.widgets || layoutData.widgets,
    settings: baseSettings,
    metadata: {
      createdAt: data.createdAt
        ? new Date(data.createdAt).getTime()
        : Date.now(),
      updatedAt: data.updatedAt
        ? new Date(data.updatedAt).getTime()
        : Date.now(),
      version: data.version || 1,
      tags: data.metadata?.tags || [],
    },
    isLocked: data.isLocked ?? false,
    variableConfig,
    group,
  };
}

export interface DashboardListResponse {
  dashboards: Dashboard[];
  totalCount: number;
  hasMore: boolean;
  userRole?: UserRoleType;
  entityId?: string;
}

export interface DashboardListOptions {
  page?: number;
  pageSize?: number;
  customerId?: string;
  search?: string;
}

/**
 * Enhanced Dashboard API client for v2 dashboard system
 */
export const dashboardApi = {
  /**
   * List dashboards with pagination and filtering
   */
  async list(
    options: DashboardListOptions = {}
  ): Promise<DashboardListResponse> {
    const params = new URLSearchParams();

    if (options.page !== undefined) {
      params.append("page", options.page.toString());
    }
    if (options.pageSize !== undefined) {
      params.append("pageSize", options.pageSize.toString());
    }
    if (options.customerId) {
      params.append("customerId", options.customerId);
    }
    if (options.search) {
      params.append("search", options.search);
    }

    const response = await apiClient.get<GoDashboardListResponse>(
      `/v1/dashboards?${params.toString()}`
    );
    // Handle Go backend response format
    const data = response.data;
    const rawDashboards = data.dashboards || [];
    return {
      dashboards: rawDashboards.map(transformBackendDashboard),
      totalCount: data.total || 0,
      hasMore: data.page < data.totalPages,
      userRole: data.userRole,
      entityId: data.entityId,
    };
  },

  /**
   * Get a specific dashboard by ID
   */
  async get(id: string): Promise<Dashboard> {
    const response = await apiClient.get<
      GoDashboardResponse | { dashboard: GoDashboardResponse }
    >(`/v1/dashboards/${id}`);
    const data =
      "dashboard" in response.data ? response.data.dashboard : response.data;
    return transformBackendDashboard(data);
  },

  /**
   * Create a new dashboard
   */
  async create(dashboard: CreateDashboardRequest): Promise<Dashboard> {
    // Include type, variableConfig, and group in settings since the database stores them in the settings JSON
    const settingsWithExtras: ExtendedSettings = {
      gridSize: 12,
      gap: 16,
      enableSnapToGrid: true,
      backgroundColor: "#ffffff",
      ...(dashboard.settings || {}),
      type: dashboard.type || "section",
      variableConfig: dashboard.variableConfig,
      group: dashboard.group,
    };

    // Store both sections and widgets in layout JSON
    const backendRequest: GoCreateDashboardRequest = {
      title: dashboard.title,
      description: dashboard.description,
      icon: dashboard.icon,
      layout: JSON.stringify({
        sections: dashboard.sections || [],
        widgets: dashboard.widgets || [],
      }),
      settings: JSON.stringify(settingsWithExtras),
      isPublic: false,
      isTemplate: false,
    };
    const response = await apiClient.post<GoDashboardResponse>(
      "/v1/dashboards",
      backendRequest
    );
    return transformBackendDashboard(response.data);
  },

  /**
   * Update an existing dashboard
   * Widgets are stored directly in the layout JSON for atomic save
   */
  async update(
    id: string,
    updates: UpdateDashboardRequest
  ): Promise<Dashboard> {
    // Fetch current dashboard to get existing settings for proper merging
    const currentDashboard = await this.get(id);

    // Include type, variableConfig, and group in settings if provided
    // IMPORTANT: Merge with existing settings to preserve unmodified values
    const hasSettingsUpdates =
      updates.settings ||
      updates.type !== undefined ||
      updates.variableConfig !== undefined ||
      updates.group !== undefined;
    const settingsWithExtras: ExtendedSettings | undefined = hasSettingsUpdates
      ? {
          // Start with existing settings
          ...currentDashboard.settings,
          // Apply any explicit settings updates
          ...(updates.settings || {}),
          // Set type - use update value, or keep existing
          type: updates.type ?? currentDashboard.type,
          // Set variableConfig - use update value if explicitly provided (even if undefined),
          // otherwise keep existing. Check using 'in' operator to distinguish undefined from not-provided.
          variableConfig:
            "variableConfig" in updates
              ? updates.variableConfig
              : currentDashboard.variableConfig,
          // Set group - use update value if explicitly provided, otherwise keep existing
          group: "group" in updates ? updates.group : currentDashboard.group,
        }
      : undefined;

    const backendRequest: GoUpdateDashboardRequest = {};
    if (updates.title !== undefined) backendRequest.title = updates.title;
    if (updates.description !== undefined)
      backendRequest.description = updates.description;
    if (updates.icon !== undefined) backendRequest.icon = updates.icon;

    // Store both sections and widgets in the layout JSON
    // Merge with existing if only one is provided
    if (updates.sections !== undefined || updates.widgets !== undefined) {
      backendRequest.layout = JSON.stringify({
        sections: updates.sections ?? currentDashboard.sections,
        widgets: updates.widgets ?? currentDashboard.widgets,
      });
    }

    if (settingsWithExtras !== undefined)
      backendRequest.settings = JSON.stringify(settingsWithExtras);
    if (updates.isLocked !== undefined)
      backendRequest.isPublic = !updates.isLocked;

    const response = await apiClient.put<GoDashboardResponse>(
      `/v1/dashboards/${id}`,
      backendRequest
    );
    return transformBackendDashboard(response.data);
  },

  /**
   * Delete a dashboard
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/v1/dashboards/${id}`);
  },

  /**
   * Clone a dashboard using the backend clone endpoint
   */
  async clone(id: string, title: string): Promise<Dashboard> {
    const response = await apiClient.post<GoDashboardResponse>(
      `/v1/dashboards/${id}/clone`,
      { title }
    );
    return transformBackendDashboard(response.data);
  },

  /**
   * Add a section to a dashboard
   */
  async addSection(
    dashboardId: string,
    section: Omit<DashboardSection, "id">
  ): Promise<Dashboard> {
    const dashboard = await this.get(dashboardId);

    const newSection: DashboardSection = {
      ...section,
      id: `section-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    };

    const updatedSections = [...dashboard.sections, newSection];

    return this.update(dashboardId, { sections: updatedSections });
  },

  /**
   * Update a section in a dashboard
   */
  async updateSection(
    dashboardId: string,
    sectionId: string,
    updates: Partial<Omit<DashboardSection, "id">>
  ): Promise<Dashboard> {
    const dashboard = await this.get(dashboardId);

    const updatedSections = dashboard.sections.map((section) =>
      section.id === sectionId ? { ...section, ...updates } : section
    );

    return this.update(dashboardId, { sections: updatedSections });
  },

  /**
   * Remove a section from a dashboard
   */
  async removeSection(
    dashboardId: string,
    sectionId: string
  ): Promise<Dashboard> {
    const dashboard = await this.get(dashboardId);

    const updatedSections = dashboard.sections.filter(
      (section) => section.id !== sectionId
    );
    const updatedWidgets = dashboard.widgets.filter(
      (widget) => widget.sectionId !== sectionId
    );

    return this.update(dashboardId, {
      sections: updatedSections,
      // Note: widgets will be handled separately in a full implementation
    });
  },

  /**
   * Bulk update dashboard sections
   */
  async updateSections(
    dashboardId: string,
    sections: DashboardSection[]
  ): Promise<Dashboard> {
    return this.update(dashboardId, { sections });
  },

  /**
   * Update dashboard settings
   */
  async updateSettings(
    dashboardId: string,
    settings: Dashboard["settings"]
  ): Promise<Dashboard> {
    return this.update(dashboardId, { settings });
  },

  /**
   * Add tags to a dashboard
   */
  async addTags(dashboardId: string, tags: string[]): Promise<Dashboard> {
    const dashboard = await this.get(dashboardId);
    const existingTags = dashboard.metadata.tags || [];
    const newTags = [...new Set([...existingTags, ...tags])];

    return this.update(dashboardId, {
      metadata: { tags: newTags },
    });
  },

  /**
   * Remove tags from a dashboard
   */
  async removeTags(dashboardId: string, tags: string[]): Promise<Dashboard> {
    const dashboard = await this.get(dashboardId);
    const existingTags = dashboard.metadata.tags || [];
    const updatedTags = existingTags.filter((tag) => !tags.includes(tag));

    return this.update(dashboardId, {
      metadata: { tags: updatedTags },
    });
  },

  /**
   * Search dashboards by various criteria
   */
  async search(query: {
    text?: string;
    tags?: string[];
    customerId?: string;
    createdAfter?: number;
    createdBefore?: number;
  }): Promise<Dashboard[]> {
    const searchParams = new URLSearchParams();

    if (query.text) {
      searchParams.append("search", query.text);
    }
    if (query.customerId) {
      searchParams.append("customerId", query.customerId);
    }

    let dashboards = await this.list({
      search: query.text,
      customerId: query.customerId,
      pageSize: 100, // Get a large batch for filtering
    });

    let results = dashboards.dashboards;

    // Apply additional client-side filters
    if (query.tags && query.tags.length > 0) {
      results = results.filter((dashboard) =>
        query.tags!.some((tag) => dashboard.metadata.tags?.includes(tag))
      );
    }

    if (query.createdAfter) {
      results = results.filter(
        (dashboard) => dashboard.metadata.createdAt >= query.createdAfter!
      );
    }

    if (query.createdBefore) {
      results = results.filter(
        (dashboard) => dashboard.metadata.createdAt <= query.createdBefore!
      );
    }

    return results;
  },

  /**
   * Get dashboard statistics
   */
  async getStats(dashboardId: string): Promise<{
    sectionCount: number;
    widgetCount: number;
    lastModified: number;
    version: number;
    size: {
      sections: number;
      widgets: number;
      totalBytes: number;
    };
  }> {
    const dashboard = await this.get(dashboardId);

    const dashboardJson = JSON.stringify(dashboard);
    const totalBytes = new Blob([dashboardJson]).size;

    return {
      sectionCount: dashboard.sections.length,
      widgetCount: dashboard.widgets.length,
      lastModified: dashboard.metadata.updatedAt,
      version: dashboard.metadata.version,
      size: {
        sections: dashboard.sections.length,
        widgets: dashboard.widgets.length,
        totalBytes,
      },
    };
  },

  /**
   * Validate dashboard structure
   */
  async validate(dashboard: Dashboard): Promise<{
    isValid: boolean;
    errors: string[];
    warnings: string[];
  }> {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Basic validation
    if (!dashboard.title?.trim()) {
      errors.push("Dashboard title is required");
    }

    if (!dashboard.id) {
      errors.push("Dashboard ID is required");
    }

    // Section validation
    dashboard.sections.forEach((section, index) => {
      if (!section.id) {
        errors.push(`Section ${index + 1} is missing an ID`);
      }
      if (!section.title?.trim()) {
        warnings.push(`Section ${section.id || index + 1} is missing a title`);
      }
    });

    // Widget validation
    dashboard.widgets.forEach((widget, index) => {
      if (!widget.id) {
        errors.push(`Widget ${index + 1} is missing an ID`);
      }
      if (!widget.sectionId) {
        errors.push(
          `Widget ${widget.id || index + 1} is not assigned to a section`
        );
      } else {
        const sectionExists = dashboard.sections.some(
          (s) => s.id === widget.sectionId
        );
        if (!sectionExists) {
          errors.push(
            `Widget ${widget.id} references non-existent section ${widget.sectionId}`
          );
        }
      }
    });

    // Settings validation
    if (dashboard.settings) {
      if (dashboard.settings.gridSize < 1 || dashboard.settings.gridSize > 24) {
        warnings.push("Grid size should be between 1 and 24");
      }
      if (dashboard.settings.gap < 0 || dashboard.settings.gap > 100) {
        warnings.push("Gap should be between 0 and 100");
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  },
};
